import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { getThemeStorageKey } from '@/core/theme/storage'
import { getBuildType } from '@/core/utils/buildType'

const getDefaultTheme = (): 'light' | 'dark' => {
	const buildType = getBuildType()
	const savedTheme = localStorage.getItem(getThemeStorageKey()) as
		| 'light'
		| 'dark'
		| null

	if (savedTheme) {
		return savedTheme
	}

	switch (buildType) {
		case 'musicbrand':
		case 'ibiza_songs':
			return 'light'
		default:
			return 'dark'
	}
}

const getDefaultLanguage = (): string => {
	const savedLanguage = localStorage.getItem('language')
	if (savedLanguage) {
		return savedLanguage
	}

	const browserLanguage = navigator.language
	const supportedLanguages = ['en-US', 'es']

	if (supportedLanguages.includes(browserLanguage)) {
		return browserLanguage
	}

	const languageCode = browserLanguage.split('-')[0]
	if (supportedLanguages.includes(languageCode)) {
		return languageCode
	}

	return 'en-US'
}

interface UIState {
	sidebarCollapsed: boolean
	theme: 'light' | 'dark'
	buildType: 'brandtrack' | 'musicbrand' | 'ibiza_songs'
	language: string
	notifications: Array<{
		id: string
		message: string
		type: 'success' | 'error' | 'info' | 'warning'
	}>
}

const initialState: UIState = {
	sidebarCollapsed: false,
	theme: getDefaultTheme(),
	buildType: getBuildType(),
	language: getDefaultLanguage(),
	notifications: [],
}

const uiSlice = createSlice({
	name: 'ui',
	initialState,
	reducers: {
		toggleSidebar: (state) => {
			state.sidebarCollapsed = !state.sidebarCollapsed
		},
		setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
			state.theme = action.payload
			localStorage.setItem(getThemeStorageKey(), action.payload)
		},
		toggleTheme: (state) => {
			const newTheme = state.theme === 'light' ? 'dark' : 'light'
			state.theme = newTheme
			localStorage.setItem(getThemeStorageKey(), newTheme)
		},
		setLanguage: (state, action: PayloadAction<string>) => {
			state.language = action.payload
			localStorage.setItem('language', action.payload)
		},
		addNotification: (
			state,
			action: PayloadAction<{
				id: string
				message: string
				type: 'success' | 'error' | 'info' | 'warning'
			}>
		) => {
			state.notifications.push(action.payload)
		},
		removeNotification: (state, action: PayloadAction<string>) => {
			state.notifications = state.notifications.filter(
				(n) => n.id !== action.payload
			)
		},
	},
})

export const {
	toggleSidebar,
	setTheme,
	toggleTheme,
	setLanguage,
	addNotification,
	removeNotification,
} = uiSlice.actions
export const uiReducer = uiSlice.reducer
